import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import InlineField from './InlineField';
import AgendaHoursCell from './AgendaHoursCell';
import StaffEditor from './StaffEditor';
import ConfirmDialog from '../../components/ConfirmDialog';
import { exportPersonnelPdf } from './accessListPdf';
import { formatTime, parseTime } from '../../utils/format';
import { useAuth } from '../../context/AuthContext';
import { canRead } from '../../utils/roles';
import haptic from '../../utils/haptic';

const KebabMenu = React.forwardRef(function KebabMenu({ items }, ref) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const btnRef  = useRef(null);
  const menuRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openAt(x, y) {
      setMenuPos({ top: Math.min(y, window.innerHeight - 220), right: Math.max(8, window.innerWidth - x) });
      setOpen(true);
    },
  }));

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e) {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!items.length) return null;

  function toggle(e) {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    }
    setOpen(o => !o);
  }

  return (
    <div className="kebab-wrap">
      <button ref={btnRef} className="kebab-btn" type="button" onClick={toggle} title="Más opciones">⋯</button>
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}>
          <div ref={menuRef} className="kebab-menu">
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                className={`kebab-item${item.danger ? ' danger' : ''}`}
                onClick={() => { setOpen(false); item.onClick(); }}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
});

// Pill de estado de la tarea. Clic (si puede operar) despliega un menú con
// Pendiente / Finalizado / Mover a mañana (igual que el StatusPicker de eventos).
function TaskStatusPicker({ status, canOperate, onSetStatus, onMoveTomorrow, tabGroup }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const pillRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) {
      if (pillRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    function onScroll() { setOpen(false); }
    document.addEventListener('mousedown', handler);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', handler);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Foco: al abrir, entra al menú (primer ítem); Escape lo cierra y vuelve al pill.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => menuRef.current?.querySelector('button')?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Mismo esquema que el StatusPicker de eventos: el pill ocupa el índice -1
  // del tabGroup de la fila, "antes" del campo 0.
  useEffect(() => {
    if (tabGroup) tabGroup.current[-1] = canOperate ? openMenu : undefined;
  });

  function openMenu() {
    if (pillRef.current) {
      const rect = pillRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: Math.min(rect.left, window.innerWidth - 190) });
    }
    setOpen(true);
  }

  function toggle(e) {
    e.stopPropagation();
    if (!canOperate) return;
    if (open) { setOpen(false); return; }
    openMenu();
  }

  function onKeyDown(e) {
    if (!canOperate) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle(e);
    }
  }

  function onMenuKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
      pillRef.current?.focus();
    }
  }

  function pick(s) {
    setOpen(false);
    pillRef.current?.focus();
    onSetStatus(s);
  }

  function moveTomorrow() {
    setOpen(false);
    pillRef.current?.focus();
    onMoveTomorrow();
  }

  const ariaLabel = `Estado: ${status}${canOperate ? ' — cambiar' : ''}`;

  const pill = (
    <span
      ref={pillRef}
      className={`status-pill ${status}${canOperate ? ' status-pill-clickable' : ''}`}
      onClick={toggle}
      onKeyDown={canOperate ? onKeyDown : undefined}
      title={canOperate ? 'Cambiar estado' : status}
      aria-label={ariaLabel}
      role={canOperate ? 'button' : undefined}
      tabIndex={canOperate ? 0 : undefined}
      aria-haspopup={canOperate ? 'menu' : undefined}
      aria-expanded={canOperate ? open : undefined}
    >
      {status}
    </span>
  );
  if (!canOperate) return pill;

  return (
    <>
      {pill}
      {open && createPortal(
        <div className="agenda-portal" style={{ position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999 }}>
          <div ref={menuRef} className="status-dropdown" role="menu" onKeyDown={onMenuKeyDown}>
            <div className="status-dropdown-label">Estado</div>
            {['pendiente', 'finalizado'].map(s => (
              <button key={s} type="button" role="menuitem" className={`status-dropdown-item task-${s}`} onClick={() => pick(s)}>
                {s}
                {status === s && <span className="status-dropdown-check">✓</span>}
              </button>
            ))}
            <button type="button" role="menuitem" className="status-dropdown-item status-move-tomorrow" onClick={moveTomorrow}>
              Mover a mañana →
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default function TaskRow({ task, index, canOperate, onLoad, onEdit, onRemoveTask, onUpdateTask, isDragging, onDragStart, onDragEnd, canDrag, personnel = [], assignmentModel = null, assignmentModelError = false, retryAssignmentModel = null, kbFocused = false, conflictKeys }) {
  const { user } = useAuth();
  const canExportPersonnelPdf = canRead(user, 'agenda.personal-pdf');
  const [toggling, setToggling] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [mobOpen, setMobOpen] = useState(false);
  const tabGroup = useRef([]);

  useEffect(() => {
    if (!kbFocused) return;
    function onEnter(e) {
      if (e.key === 'Enter') { e.preventDefault(); tabGroup.current[0]?.(); }
    }
    document.addEventListener('keydown', onEnter);
    return () => document.removeEventListener('keydown', onEnter);
  }, [kbFocused]);

  async function saveField(field, val) {
    try {
      await api.put(`/tasks/${task.id}`, { [field]: val || null });
      onUpdateTask?.({ [field]: val || null });
    } catch {
      onLoad();
    }
  }

  async function setStatus(newStatus) {
    if (!canOperate || toggling || newStatus === task.status) return;
    setToggling(true);
    try {
      await api.patch(`/tasks/${task.id}/status`, { status: newStatus });
      onUpdateTask?.({ status: newStatus });
    } catch (e) {
      console.error(e);
      onLoad();
    } finally { setToggling(false); }
  }

  // Mover a mañana: pasa la tarea al día siguiente conservando horarios (columnas
  // del task) y personas (asignaciones por task_id). El memo tasksByDate la
  // re-agrupa sola en el nuevo día.
  async function moveToTomorrow() {
    const base = task.task_date?.slice(0, 10);
    if (!base) return;
    const [y, m, d] = base.split('-').map(Number);
    const nd = new Date(y, m - 1, d + 1);
    const tomorrow = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}-${String(nd.getDate()).padStart(2, '0')}`;
    try {
      await api.put(`/tasks/${task.id}`, { task_date: tomorrow });
      onUpdateTask?.({ task_date: tomorrow, sort_order: null });
    } catch (e) { console.error(e); onLoad(); }
  }

  async function handleDelete() {
    try {
      await api.delete(`/tasks/${task.id}`);
      onRemoveTask?.(task.id);
    } catch (e) { console.error(e); }
  }

  const done         = task.status === 'finalizado';
  const zebraClass   = index % 2 === 0 ? 'task-even' : 'task-odd';
  const doneClass    = done ? ' task-done' : '';
  const pendingClass = task.status === 'pendiente' ? ' task-pending' : '';
  const draggingClass = isDragging ? ' is-dragging' : '';
  const focusClass    = kbFocused ? ' row-kb-focus' : '';

  const kebabRef = useRef(null);
  const menuTimer = useRef(null);
  const menuPressPos = useRef(null);
  const menuFired = useRef(false);
  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  function toggleMob(e) {
    if (!isMobile()) return;
    if (menuFired.current) { menuFired.current = false; return; }
    if (e.target.closest('button, a, input, textarea, select, .task-check, .status-pill, .kebab-wrap, .agenda-portal')) return;
    setMobOpen(o => !o);
  }
  function rowPointerDown(e) {
    if (!isMobile()) return;
    menuFired.current = false;
    if (e.target.closest('.inline-trigger.editable, button, a, input, textarea, select, .task-check, .status-pill, .se-line, .se-sheet, .se-role-pop, .agenda-portal')) return;
    const x = e.clientX, y = e.clientY;
    menuPressPos.current = { x, y };
    clearTimeout(menuTimer.current);
    menuTimer.current = setTimeout(() => { menuFired.current = true; haptic.tick(); kebabRef.current?.openAt(x, y); }, 500);
  }
  function rowPointerMove(e) {
    if (!menuPressPos.current) return;
    if (Math.abs(e.clientX - menuPressPos.current.x) > 10 || Math.abs(e.clientY - menuPressPos.current.y) > 10) clearTimeout(menuTimer.current);
  }
  function rowPointerEnd() { clearTimeout(menuTimer.current); menuPressPos.current = null; }

  return (
    <div
      className={`task-row ${zebraClass}${doneClass}${pendingClass}${draggingClass}${focusClass}${mobOpen ? ' ag-mob-open' : ''}`}
      data-task-id={task.id}
      onClick={toggleMob}
      onPointerDown={rowPointerDown}
      onPointerMove={rowPointerMove}
      onPointerUp={rowPointerEnd}
      onPointerLeave={rowPointerEnd}
    >
      {/* Col 1 — drag handle */}
      <div className="task-cell task-cell-order">
        <span
          className="ev-drag-handle"
          title="Reordenar"
          draggable={canDrag}
          onDragStart={canDrag ? e => {
            const rowEl = e.currentTarget.closest('[data-task-id]');
            const cells = rowEl ? Array.from(rowEl.children) : [];
            const isLight = document.documentElement.dataset.theme === 'light';
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('application/x-depot-task', '');
            e.dataTransfer.setData('text/plain', JSON.stringify({ id: task.id, sourceDate: task.task_date?.slice(0, 10) }));
            if (cells.length) {
              const visual = [];
              for (const c of cells) {
                if (window.getComputedStyle(c).display === 'contents') {
                  for (const gc of c.children) visual.push(gc);
                } else {
                  visual.push(c);
                }
              }
              const rects = visual.map(c => c.getBoundingClientRect());
              const colWidths = rects.map(r => `${Math.round(r.width)}px`);
              const totalWidth = rects.reduce((s, r) => s + r.width, 0);
              const scopeWrap = document.createElement('div');
              if (isLight) scopeWrap.dataset.theme = 'light';
              scopeWrap.style.cssText = 'position:fixed;top:-9999px;left:0;pointer-events:none;';
              const scopePage = document.createElement('div');
              scopePage.className = 'agenda-page';
              const ghost = document.createElement('div');
              Object.assign(ghost.style, {
                display: 'grid',
                gridTemplateColumns: colWidths.join(' '),
                width: `${Math.round(totalWidth)}px`,
                borderRadius: '4px', overflow: 'hidden',
                opacity: '0.93', pointerEvents: 'none',
              });
              visual.forEach(cell => {
                const clone = cell.cloneNode(true);
                clone.style.background = window.getComputedStyle(cell).backgroundColor;
                ghost.appendChild(clone);
              });
              scopePage.appendChild(ghost);
              scopeWrap.appendChild(scopePage);
              document.body.appendChild(scopeWrap);
              e.dataTransfer.setDragImage(ghost, e.clientX - rects[0].left, e.clientY - rects[0].top);
              setTimeout(() => scopeWrap.parentNode?.removeChild(scopeWrap), 0);
            }
            onDragStart?.();
          } : undefined}
          onDragEnd={() => onDragEnd?.()}
        >≡</span>
      </div>

      {/* Col 2 — Nombre / Evento */}
      <div className="task-cell task-cell-title">
        <div className="task-title-header">
          <div className="task-title-content">
            <div className="task-title-row">
              <span
                className="task-title-text"
              >
                <InlineField
                  value={task.title || ''}
                  onSave={v => saveField('title', v)}
                  canEdit={false}
                  placeholder="Sin título"
                />
              </span>
            </div>
            {task.event_name && (
              <div className="task-event-chip">{task.event_name}</div>
            )}
            <div className="task-status-line">
              <TaskStatusPicker
                status={task.status}
                canOperate={canOperate}
                onSetStatus={setStatus}
                onMoveTomorrow={moveToTomorrow}
                tabGroup={tabGroup}
              />
            </div>
          </div>
        </div>
      </div>

      <AgendaHoursCell
        className="task-cell task-cell-hours"
        rows={[
          { key: 'start', label: 'INI', value: formatTime(task.time), onSave: v => saveField('time', parseTime(v)), canEdit: canOperate, tabGroup, tabIndex: 1 },
          { key: 'end', label: 'FIN', value: formatTime(task.end_time), onSave: v => saveField('end_time', parseTime(v)), canEdit: canOperate, tabGroup, tabIndex: 2 },
        ]}
      />

      {/* Col 4 — Armado (no aplica a tareas) */}
      <div className="task-cell task-cell-placeholder" style={{ gridColumn: '4 / 5' }} />

      {/* Col 5 — Responsable (va en Operación) */}
      <div className="task-cell task-cell-staff" style={{ gridColumn: '5 / 6' }}>
        <StaffEditor
          taskId={task.id}
          initialAssignments={task.staff_assignments || []}
          initialSections={task.staff_sections || []}
          assignmentModel={assignmentModel}
          assignmentModelError={assignmentModelError}
          retryAssignmentModel={retryAssignmentModel}
          personnel={personnel}
          canEdit={canOperate}
          entityDate={task.task_date?.slice(0, 10)}
          startTime={task.time?.slice(0, 5)}
          endTime={task.end_time?.slice(0, 5)}
          externalConflictKeys={conflictKeys}
        />
      </div>

      {/* Col 6 — Desarmado (no aplica a tareas) */}
      <div className="task-cell task-cell-placeholder" style={{ gridColumn: '6 / 7' }} />

      {/* Col 7 — Notas */}
      <div
        className="task-cell task-cell-notes"
        onClick={canOperate ? () => tabGroup.current[4]?.() : undefined}
        style={{ gridColumn: '7 / 8', cursor: canOperate ? 'text' : undefined }}
      >
        <InlineField
          value={task.notes || ''}
          onSave={v => saveField('notes', v)}
          canEdit={canOperate}
          multiline
          tabGroup={tabGroup}
          tabIndex={4}
          placeholder=""
        />
      </div>

      {/* Col 10 — Acciones */}
      <div className="task-cell task-cell-actions" style={{ gridColumn: '8 / 9' }}>
        <KebabMenu ref={kebabRef} items={[
          ...(canExportPersonnelPdf ? [
            { label: 'PDF Personal', onClick: () => exportPersonnelPdf({ entityType: 'task', entityId: task.id }) },
          ] : []),
          ...(canOperate ? [
            { label: 'Editar', onClick: onEdit },
            { label: 'Eliminar', onClick: () => setConfirmDelete(true), danger: true },
          ] : []),
        ]} />
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Eliminar tarea"
          message={`¿Eliminar "${task.title}"? Esta acción no se puede deshacer.`}
          confirmLabel="Eliminar"
          danger
          onConfirm={() => { setConfirmDelete(false); handleDelete(); }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}
    </div>
  );
}
